import { ProjectSocketContext } from 'contexts/ProjectSocketContext';
import useInput from 'lib/hooks/useInput';
import useKeyboard from 'lib/hooks/useKeyboard';
import { getFileDataUrl } from 'lib/library';
import { useDidUpdateEffect } from 'lib/utils';
import _ from 'lodash';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { UserActions, ProjectActions } from 'store/actionCreators';
import { fetchGoogleTranslate } from 'api/util';
import { useShallowAppSelector } from 'store/hooks';
import useMultiFileInput from 'lib/hooks/useMultiFileInput';
import { v4 as uuid } from 'uuid';
import storage, { keys } from 'lib/storage';
import ProjectChat from 'components/project/ProjectChat';

interface Message {
  id: string;
  message: string;
  transMessage: string;
  language: string;
  active: boolean;
}

interface ProjectChatContainerProps {
  isShowChat: boolean;
  onClickChat: () => void;
}

export default function ProjectChatContainer({
  isShowChat,
  onClickChat,
}: ProjectChatContainerProps) {
  const {
    user,
    language,
    hasAlarm,
    projectInfo,
    uploadChatFileSuccess,
    editChatFileSuccess,
    deleteChatFile,
    deleteChatFileSuccess,
    fetchChatFileList,
    fetchChatFileSuccess,
  } = useShallowAppSelector(state => ({
    user: state.user.user,
    language: state.base.language,
    hasAlarm: state.project.hasAlarm,
    projectInfo: state.project.project.data?.projectInfo,
    uploadChatFileSuccess: state.project.uploadChatFile.success,
    editChatFileSuccess: state.project.editChatFile.success,
    deleteChatFile: state.project.deleteChatFile.data,
    deleteChatFileSuccess: state.project.deleteChatFile.success,
    fetchChatFileList: state.project.fetchChatFile.data?.list,
    fetchChatFileSuccess: state.project.fetchChatFile.success,
  }));
  const { pcode } = useParams() as { pcode: string };
  const projectCode = projectInfo?.projectCode;
  const talkBoxListScrollRef = useRef<HTMLDivElement | null>(null);
  const message = useInput('');
  const page = useInput(1);
  const userSyncUid = user?.syncUid;
  const [transMessageList, setTransMessageList] = useState<Message[]>([]);
  const [prevLanguage, setPrevLanguage] = useState(language);
  const uploadFileList = useMultiFileInput([]);
  const [chatDataCt, setChatDataCt] = useState<any>([]);
  const [chatFileList, setChatFileList] = useState<any[]>([]);
  const [notSupportFiles, setNotSupportFiles] = useState<string[]>([]);
  const isAskAgain = useInput(false);
  const isNoRefeat = user?.isNoRepeat;
  const isFilePopup = useInput(false);
  const { ipcRenderer } = window;
  const keyboard = useKeyboard();
  const [newWindow, setNewWindow] = useState<Window | null>(null);
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const [isAllImagesLoaded, setAllImagesLoaded] = useState(false);
  const [imageLength, setImageLength] = useState(0);

  const {
    socketState,
    chatUserData,
    chatListData,
    chatData,
    socketId,
    chatDataImgPrev,
    chatRemoveFileId,
    chatListRequestPossible,
    lastUnreadMessageId,
  } = useContext(ProjectSocketContext);

  const isPossibleChat = !!hasAlarm ? !hasAlarm : !!projectInfo?.partnerGroupCode;

  useEffect(() => {
    ProjectActions.fetch_chat_file_request({ projectCode: projectInfo?.projectCode });
  }, [projectCode]);

  const handleImagePopup = (imageId: string) => {
    const url = `${window.location.origin}/util/images/${pcode}/${imageId}`;
    if (!ipcRenderer) {
      const popupWindow = window.open(url, '_blank', 'width=760,height=470');
      setNewWindow(popupWindow);
    } else {
      ipcRenderer.send('showProjectChatViewer', {
        width: 600,
        height: 600,
        url: url,
      });
    }
  };

  // NOTE: google translation
  const translationGoogle = async (text: string) => {
    try {
      const { data } = await fetchGoogleTranslate({
        text,
        target: language.toLowerCase(),
      });
      return data?.translateInfo?.translation || null;
    } catch (error) {
      console.log('translationGoogle error', error);
    }
  };

  const handleTranslation = async (data: { id: string; message: string }) => {
    const { id, message } = data;

    const currentTransItem = transMessageList.find(item => item.id === id);

    // NOTE: 변경 안될 단어 등록 후
    // 언어 동일한 조건하에서 translate call 한 후에는 변경된 텍스트로 노출, 같은 내용을 다시 call을 하지 않도록
    // 언어가 변경됐을 경우 다시 translate call 하도록
    // e.g. <span translate="no">Crown</span>
    let transMessage = '';
    // 설정한 언어가 변경되었을 경우 새롭게 call
    const isChangeLanguage =
      currentTransItem?.language &&
      (prevLanguage !== currentTransItem?.language || prevLanguage !== language);
    if (isChangeLanguage) {
      transMessage = await translationGoogle(message);
      setPrevLanguage(language);
    } else {
      // 처음 생성시 경로
      transMessage = currentTransItem?.transMessage || (await translationGoogle(message));
    }

    setTransMessageList(draft => {
      const currentItem = draft.find(item => item.id === id);
      if (currentItem) {
        return draft.map(item => {
          if (item.id === id) {
            item.active = !item.active;
            if (isChangeLanguage) {
              item.transMessage = transMessage;
              item.language = language;
            }
          }
          return item;
        });
      }
      const obj = {
        id,
        message,
        transMessage,
        language,
        active: true,
      };

      return draft.concat(obj);
    });
  };

  const chatMessageList = useMemo(() => {
    return chatData?.filter((item: any) => !!item?.message);
  }, [chatData]);

  // chatRoomId 연결
  let submitChatListData = useMemo(() => {
    return {
      chatRoomId: chatUserData?.result?._id,
      page: page.value,
      skip: chatMessageList?.length || 0,
      projectCode: projectCode,
    };
  }, [chatUserData, page.value, chatMessageList, projectCode]);
  // 페이지 첫 로딩시, chatUserData받은 후 기존 채팅 page 1 로딩
  useDidUpdateEffect(() => {
    if (!!chatUserData?.result?._id) {
      socketState.current.emit('chatList', submitChatListData);
    }
  }, [chatUserData?.result?._id]);

  const handleSendMessage = () => {
    if (!message.value?.trim()) return;

    const sendData = {
      chatRoomId: chatUserData?.result?._id,
      message: message.value,
      projectCode: chatUserData?.result?.projectCode,
      projectName: projectInfo?.projectName,
      syncUid: userSyncUid,
      ownerGroupCode: projectInfo?.ownerGroupCode || '',
      partnerGroupCode: projectInfo?.partnerGroupCode || '',
    };
    socketState.current.emit('chatSend', sendData);
    // 요청 이후
    message.setValue('');
    // TODO: 채팅 scrolltop 왜 하는지 체크해야함
    if (talkBoxListScrollRef?.current?.scrollTop) {
      console.log('scroll top block ', talkBoxListScrollRef?.current?.scrollTop);
      talkBoxListScrollRef.current.scrollTop = 0;
    }
  };

  // onScroll
  const handleScrollChat = (e: any) => {
    const chatListHeight = 530;
    const target = e.currentTarget;
    if (!target?.scrollTop) return;

    const targetScrollTop = Math.abs(target.scrollTop);
    const requestOffset = (target.scrollHeight - chatListHeight) * 0.8;
    if (targetScrollTop > requestOffset && chatListRequestPossible.value) {
      const nextPage = page.value + 1;
      submitChatListData = {
        ...submitChatListData,
        page: nextPage,
      };
      chatListRequestPossible.setValue(false);
      if (chatListData?.hasNext) {
        socketState.current.emit('chatList', submitChatListData);
        page.setValue(nextPage);
      }
    }
  };

  const checkFileStatus = (files: any[]) => {
    // * JPG, GIF, or PNG only. Max size of 10MB
    // You can only register up to 10 portfolio files.
    // Also,  the file name can only be registered up to 128 byte.
    const typeList = ['jpg', 'jpeg', 'png'];
    let returnFlag = true;

    const failList = [];
    for (let i = 0; i < files.length; i++) {
      const fileType = files[i].name.slice(files[i].name.lastIndexOf('.') + 1);
      const fileName = files[i].name.split('.')[0];
      if (
        !typeList.find(type => type === fileType.toLowerCase()) ||
        files[i].size > 10485760 ||
        fileName.length + fileType.length > 127
      ) {
        returnFlag = false;
        failList.push(files[i].name);
        // break;
      }
    }
    return failList;
  };

  const handleClickFileUpload = (e: any) => {
    // Drag&Drop을 통해 전달받은 파일과 포멧이 다라 포멧변경
    const {
      target: { files },
    } = e;
    const fileList = Object.keys(files).reduce((acc, key) => {
      files[key].id = uuid();
      return acc.concat(files[key]);
    }, []);
    handleFileData(fileList);
  };

  const handleAddLocalFiles = async (data: any) => {
    const { dropFiles } = data;
    handleFileData(dropFiles);
  };

  const handleFileData = async (fileData: any) => {
    if (checkFileStatus(fileData).length > 0) {
      setNotSupportFiles([...checkFileStatus(fileData)]);
    }
    fileData.map(async (item: any) => {
      const thumbNail = await getFileDataUrl(item);
      // 채팅데이터에 등록되는 파일과 비슷한 포멧으로 수정
      const obj = {
        thumb: thumbNail,
        file: item,
        path: item.path,
        name: item.name,
        size: item.size,
        type: item.type,
        originalname: item.name,
        status: 'init',
      };
      uploadFileList.setValue((draft: any) => draft.concat(obj));
    });
  };

  const handleUploadFileList = () => {
    isFilePopup.setValue(false);

    const formData = new FormData();
    uploadFileList.value.map((item: any) => {
      formData.append('images', item.file);
    });
    formData.append('socketId', socketId);

    const submitData = {
      projectCode: projectCode,
      data: formData,
    };

    ProjectActions.upload_chat_file_request(submitData);

    const changeDataFormat = uploadFileList.value.reverse().map((item: any) => {
      // 이미지 업로드 시, 업로드 데이터가 실제로 서버에 올라가기전 임시로 보여줄 파일 필요
      // 해당 파일을 채팅 데이터에 필요한 포멧으로 변경하여, 채팅 데이터로 전달
      const previewChatImage = {
        images: [item],
        imagesId: '',
        sender: userSyncUid,
        temp: {
          file: item.file,
          thumb: item.thumb,
        },
      };
      return previewChatImage;
    }, []);
    chatDataImgPrev.setValue([...changeDataFormat]);
    uploadFileList.setValue([]);
  };

  const handleClickTransfer = () => {
    if (isAskAgain.value) {
      const noRepeatUser = storage.get(keys.user_no_repeat) || '';
      const addNoRepeatUser = new Set([...noRepeatUser, user.email]);
      storage.set(keys.user_no_repeat, [...addNoRepeatUser]);
      setTimeout(() => {
        UserActions.fetch_profile_request();
      }, 100);
    }
    handleUploadFileList();
  };

  const handleRetryUploadFile = (data: any) => {
    if (!data.temp.file) return;
    const formData = new FormData();
    formData.append('image', data?.temp.file);
    formData.append('chatId', data?._id);
    formData.append('imageId', data?.temp.imageId);
    formData.append('socketId', socketId);

    const submitData = {
      projectCode: projectCode,
      data: formData,
    };
    chatDataImgPrev.setValue([data]);
    ProjectActions.edit_chat_file_request(submitData);
  };

  const handleRemoveFailureFile = (data: any) => {
    const submitData = {
      projectCode: projectCode,
      data: {
        chatId: data._id,
      },
    };
    ProjectActions.delete_chat_file_request(submitData);
  };

  const handleClickCancel = () => {
    uploadFileList.setValue([]);
    setNotSupportFiles([]);
  };

  useEffect(() => {
    ProjectActions.fetch_chat_file_request({ projectCode: projectInfo?.projectCode });
  }, [projectCode]);

  useEffect(() => {
    // 업로드 실패 시, 채팅 정렬 변경 (실패 파일 맨 앞으로)
    let successFileList: any[] = [];
    let failFileList: any[] = [];
    chatData.map((item: any) => {
      if (item.eventType === 'enter' || !item?.images || item.images[0].status !== 'fail') {
        successFileList.push(item);
      } else {
        failFileList.push(item);
      }
    });
    setChatDataCt([...failFileList, ...successFileList]);
  }, [chatData]);

  useEffect(() => {
    if (uploadFileList.value.length > 0) {
      if (notSupportFiles.length > 0) {
        // 지원하지 않는 형식이 포함된 경우
        isFilePopup.setValue(true);
      } else {
        if (isNoRefeat) {
          isFilePopup.setValue(false);
          //파일 전송
          handleUploadFileList();
        } else {
          isFilePopup.setValue(true);
        }
      }
    } else {
      isFilePopup.setValue(false);
    }
  }, [uploadFileList.value, notSupportFiles, isNoRefeat]);

  const handleLoadImage = () => {
    setImagesLoaded(prevImagesLoaded => prevImagesLoaded + 1);
  };

  useDidUpdateEffect(() => {
    if (imagesLoaded === imageLength) {
      setAllImagesLoaded(true);
    }
  }, [imagesLoaded, imageLength]);

  useEffect(() => {
    const lastUnreadIdx = chatDataCt.findIndex((item: any) => item._id === lastUnreadMessageId);
    const images = chatDataCt.filter(
      (value: any, idx: number) => !!value.images && lastUnreadIdx >= idx,
    );

    setImageLength(images.length);
  }, [chatDataCt]);

  useDidUpdateEffect(() => {
    if (!fetchChatFileSuccess) return;
    setChatFileList([...fetchChatFileList]);
  }, [fetchChatFileSuccess]);

  useDidUpdateEffect(() => {
    if (!uploadChatFileSuccess) return;
    chatDataImgPrev.setValue([]);
    uploadFileList.setValue([]);
  }, [uploadChatFileSuccess]);

  useDidUpdateEffect(() => {
    if (!editChatFileSuccess) return;
    chatDataImgPrev.setValue([]);
    uploadFileList.setValue([]);
  }, [editChatFileSuccess]);

  useDidUpdateEffect(() => {
    if (!deleteChatFileSuccess) return;
    chatRemoveFileId.setValue(deleteChatFile.chatId);
    chatDataImgPrev.setValue([]);
    uploadFileList.setValue([]);
  }, [deleteChatFileSuccess]);

  return (
    <ProjectChat
      isShowChat={isShowChat}
      chatFileList={chatFileList}
      chatDataCt={chatDataCt}
      language={language}
      talkBoxListScrollRef={talkBoxListScrollRef}
      message={message}
      transMessageList={transMessageList}
      uploadFileList={uploadFileList}
      notSupportFiles={notSupportFiles}
      isAskAgain={isAskAgain}
      isFilePopup={isFilePopup}
      onScrollChat={handleScrollChat}
      onSendMessage={handleSendMessage}
      onTranslation={handleTranslation}
      onClickFileUpload={handleClickFileUpload}
      onClickTransfer={handleClickTransfer}
      onClickCancel={handleClickCancel}
      onAddLocalFiles={handleAddLocalFiles}
      onRetryUploadFile={handleRetryUploadFile}
      onRemoveFailureFile={handleRemoveFailureFile}
      onClickChat={onClickChat}
      isAllImagesLoaded={isAllImagesLoaded}
      projectCode={projectCode}
      isPossibleChat={isPossibleChat}
      onImagePopup={handleImagePopup}
      onLoadImage={handleLoadImage}
      keyboard={keyboard}
    />
  );
}
